import moment from 'moment';
import { addUuidBatch } from '@gp/utils/common';
import { getNullableMoment } from '@gp/utils/date';
import { getWeekDateOfDPSReport, formatComment } from '../../utils';

import type { IDPSReportCalendarDTO } from '@gp/types/dpsReport/common';
import type { IDPSReportDTOV3, IDPSReportUpdatesOfTheWeekDTO, IDPSReportVOV3 } from '@gp/types/dpsReport/v3';

export const getCalendarEndDate = (startDate?: string): string => {
  if (!startDate) return '';
  const startDateM = moment(startDate);
  if (!startDateM.isValid()) return '';
  return startDateM.add(2, 'months').startOf('month').format('YYYY-MM-DD');
};

const formatCalendars = (list?: IDPSReportCalendarDTO[] | null): IDPSReportCalendarDTO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => ({ ...item, status: item?.status?.trim() || '' }));
};

const formatChanges = (list?: IDPSReportUpdatesOfTheWeekDTO[] | null): IDPSReportUpdatesOfTheWeekDTO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const { changes } = item || {};
    const changesTmp = changes?.map((subItem) => {
      const { content } = subItem || {};
      const contentTmp = content?.map((ssubItem) => ssubItem?.trim() || '')?.filter((ssubItem) => !!ssubItem);
      return { ...subItem, content: contentTmp || [] };
    });
    return { ...item, changes: changesTmp || [] };
  });
};

/**
 * 转换DPS周报（DTO）为 DPS周报（VO）
 * @param dpsReport DPS周报（DTO）
 * @return DPS周报（VO）
 */
export const formatDPSReport = (dpsReport?: IDPSReportDTOV3 | null): IDPSReportVOV3 | null => {
  if (!dpsReport) return null;

  const {
    comment,
    project_comment: projectComment,
    changes,
    memo,
    calendar: calendarsData,
    start_date: startDate,
    end_date: endDate,
  } = dpsReport;
  const { start_date: calendarsStartDate, end_date: calendarsEndDate, list: calendars } = calendarsData || {};

  // 转换接口返回的开始日期、结束日期 为 符合DPS周报要求的日期（星期一～星期五）
  const {
    startDateM: startDateMDPSReportWeek,
    startDate: startDateDPSReportWeek,
    endDate: endDateDPSReportWeek,
  } = getWeekDateOfDPSReport(startDate, endDate);

  const commentTmp = formatComment(comment);

  const projectCommentTmp = addUuidBatch(projectComment);

  const changesTmp = addUuidBatch(formatChanges(changes));

  const memoTmp = addUuidBatch(memo);

  const calendarsTmp = addUuidBatch(formatCalendars(calendars));

  return {
    ...dpsReport,
    start_date_dps_report_week: startDateDPSReportWeek,
    end_date_dps_report_week: endDateDPSReportWeek,
    year: startDateMDPSReportWeek?.get('year') || Number.NaN,
    weeks_in_year: startDateMDPSReportWeek?.weeks() || Number.NaN,
    comment: commentTmp || '',
    project_comment: projectCommentTmp || [],
    changes: changesTmp || [],
    memo: memoTmp || [],
    calendar: {
      start_date: calendarsStartDate || '',
      end_date: calendarsEndDate || '',
      list: calendarsTmp || [],
    },
  };
};

const SHOW_GR_DATE_START_DATE = '2025-03-31';

/**
 * 判断DPS Newsletter周报的Memo模块是否显示GR Date列
 * 2025-03-28迭代上线以前的周报的Memo模块 预览、编辑 均不展示GR Date信息，该时间点以后的周报的Memo模块 预览、编辑 均要展示GR Date信息
 * 由于2025-03-31是该迭代上线后的第一周的周报，所以方法内用2025-03-31作为最小始日期判断
 * tapd: https://tapd.tencent.com/tapd_fe/20427644/story/detail/1020427644121780372?from_iteration_id=1020427644002114219
 * @param startDate 周报的开始日期
 * @return boolean 是否展示GR Date信息
 */
export const getIsShowGRDate = (startDate?: string): boolean => {
  const startDateM = getNullableMoment(startDate);
  return !!startDateM?.isValid() && startDateM.isSameOrAfter(SHOW_GR_DATE_START_DATE);
};


